import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Gallery.css";
import { nftService } from "../../../services";
import type { NFTMetadata } from "../../../services";
import { ColorRing } from "react-loader-spinner";
import { NFT } from "../../../services/models/NFT";
import ProgressiveImage from "react-progressive-graceful-image";
import imageSizeOne from "../../../assets/icon-size-1.png"
import imageSizeTwo from "../../../assets/icon-size-2.png"
import imageSizeThree from "../../../assets/icon-size-3.png"

const Gallery = () => {
  const [nfts, setNFTs] = useState<NFT[]>([]);
  const [filteredNFTs, setFilteredNFTs] = useState<NFT[]>([]);
  const [chapters, setChapters] = useState<String[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<String | null>("All");
  const [resize, setResize] = useState<string | undefined>("one");
  const [resizeImage, setResizeImage] = useState<string | undefined>("one-image");
  const [showChapters, setShowChapters] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const filterItem = (chapter: String) => {
    const updateItems = nfts.filter((curElem) => {
      return curElem.collection === chapter;
    });

    if(chapter === "All"){
      setFilteredNFTs(nfts);
    }
    else
    {
      setFilteredNFTs(updateItems);
    }
    setActive(chapter);
    
    // Scroll to top when chapter is selected
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const resizePictures = (size: string) => {
    setResize(size);
    const swImgSize = resizeTranslater(size);
    setResizeImage(swImgSize);
  }

  const resizeTranslater = (size: string) => {
    switch (size) {
      case "one":
        return "one-image";
      case "two":
        return "two-image";
      case "three":
        return "three-image";
      default:
        return "one-image";
    }
  }


  const chapterSubstring = (chapter: String) => {
    const ch = chapter.split("-").pop() ?? "";
    const capitalized = ch.charAt(0).toUpperCase() + ch.slice(1);
    return capitalized;
  }

  const toggleChapters = () => {
    setShowChapters(!showChapters);
  }

  // Convert NFTMetadata to NFT format
  const convertToNFTFormat = (nftMetadata: NFTMetadata[]): NFT[] => {
    return nftMetadata.map((nft) => ({
      identifier: nft.identifier,
      name: nft.name,
      collection: nft.collection,
      contract: nft.contract,
      description: nft.description,
      image_url: nft.display_image_url || nft.image_url
    }));
  };

  const fetchNFTs = useCallback(async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      const nftMetadataList = await nftService.getNFTs(1000, forceRefresh);
      const nftList = convertToNFTFormat(nftMetadataList);
      
      if (nftList.length === 0) {
        setError('No NFTs found. Please try again.');
        setLoading(false);
        return;
      }
      
      setNFTs(nftList);
      setFilteredNFTs(nftList);
      let unique_values = [
        ...new Set(nftList.map((element) => element.collection)),
      ];
      setChapters(unique_values);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching NFTs from ColorFuel API:', error);
      setError('Failed to load NFTs. Please try again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNFTs(false);
  }, [fetchNFTs]);

  // Scroll detection for hover effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
    <div className="gallery-content">
      <div className={`gallery-options ${isScrolled ? 'scrolled' : ''}`}>
        <div className="gallery-filter">
          {
            !loading ? (
              <button onClick={() => filterItem("All")} className={active === "All" ? "button all-btn selected" : "button all-btn"}>
                <div className="chapters-text">
                All
                </div>
              </button>
            ) : null
          }
          {
            !loading ? (
              <div className={`chapter-buttons-container ${showChapters ? 'expanded' : 'collapsed'}`}>
                {chapters.map((c: String, i: number) => (
                  <button key={i} onClick={() => filterItem(c)} className={active === c ? "button selected" : "button"}>
                    <div className="chapters-text">
                      {chapterSubstring(c)}
                    </div>
                  </button>
                ))}
              </div>
            ) : null
          }
          {
            !loading ? (
              <button onClick={toggleChapters} className="button toggle-chapters-btn">
                {showChapters && (
                  <div className={`toggle-arrow ${showChapters ? 'arrow-left' : 'arrow-right'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" stroke-width="2">
                      <path d="M8 4L20 12L8 20" stroke="white" stroke-width="2" fill="none" />
                    </svg>
                  </div>
                )}
                <span className="chapters-text">Chapters</span>
                {!showChapters && (
                  <div className={`toggle-arrow ${showChapters ? 'arrow-left' : 'arrow-right'}`}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="white" stroke-width="2">
                      <path d="M8 4L20 12L8 20" stroke="white" stroke-width="2" fill="none" />
                    </svg>
                  </div>
                )}
              </button>
            ) : null
          }
        </div>
        {
        !loading ? (
          <div className="gallery-size">
              <div onClick={() => resizePictures("one")} className={resize === "one" ? "button selected" : "button"}>
                <img className="size-icon-one" src={imageSizeOne} alt="size icon one" />
              </div>
              <div onClick={() => resizePictures("two")} className={resize === "two" ? "button selected" : "button"}>
                <img className="size-icon-two" src={imageSizeTwo} alt="size icon two" />
              </div>
              <div onClick={() => resizePictures("three")} className={resize === "three" ? "button selected" : "button"}>
                <img className="size-icon-three" src={imageSizeThree} alt="size icon three" />
              </div>
          </div>
        ) : null
        }
      </div>
      <div className={`gallery-container ${resize === "three" ? "original-size-mode" : ""} ${resize === "two" ? "small-size-mode" : ""}`}>
        {loading ? (
            <ColorRing
            visible={true}
            height="160"
            width="160"
            ariaLabel="color-ring-loading"
            wrapperStyle={{
              marginTop: '100px',
              marginBottom: '100px'
            }}
            wrapperClass="color-ring-wrapper"
            colors={['#e15b64', '#f47e60', '#f8b26a', '#abbd81', '#849b87']}
            />
        ) : error ? (
          <div style={{
            textAlign: 'center',
            padding: '100px 20px',
            color: 'white'
          }}>
            <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>{error}</p>
            <button 
              onClick={() => fetchNFTs(true)} 
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#4a90e2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          filteredNFTs?.map((art: NFT, index: number) => (
            <div key={index} className={resizeImage}>
              <Link to={`/discover/ethereum/nft/${encodeURIComponent(art.name)}`} className="image-container shine">
                <div className={"image-content " + resize}>
                      <ProgressiveImage
                    src={art.image_url}
                    placeholder="./colorfuel-logo.jpg"
                  >
                    {(src, loading) => (
                      <img
                        className={`image${loading ? " loading " : " loaded "}` +resize}
                        src={src}
                        alt="nft"
                      />
                    )}
                  </ProgressiveImage>
                  </div>
                <div className="image-text-box">
                  <h3 className={`image-text ${resizeImage?.replace('-image', '-text') || 'one-text'}`}>{art.name?.replace(/^#\d+\s*/, '') || 'Unnamed NFT'}</h3>
                </div>
              </Link>
            </div>
          ))
        )}
      </div>
    </div>
    </>
  );
};

export default Gallery;
